import { Icon, IconButton, makeStyles, Snackbar, SnackbarContent } from '@material-ui/core';
import React, { ReactComponentElement } from 'react';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { IReactChildren, IWithStylesProps } from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { whiteColor } from '~/src/dash/utils/material-dashboard-react';

export interface ISnackbarProps extends IWithStylesProps, IReactChildren {
  open: boolean;
  // different styled snackbar messages display depending on if there is an error or warning
  error?: boolean;
  warning?: boolean;
  info?: boolean;
  processing?: boolean;
  onClose?: () => void;
  enableTopPosition?: boolean;
  enableCenterPosition?: boolean;
  successBar?: boolean;
  messageColor?: string;
  backgroundColor?: string;
  closeColor?: string;
  autoHideDuration?: number;
}

export interface ISnackbarState {
  error: boolean;
  warning: boolean;
  message: string;
}

export const generateSnackbarState = (): ISnackbarState => {
  return {
    error: false,
    warning: false,
    message: '',
  };
};

const useStyles = makeStyles((theme: any) => ({
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  warning: {
    backgroundColor: whiteColor,
  },
  info: {
    backgroundColor: publicaColors.navy,
  },
  success: {
    backgroundColor: publicaColors.navy,
  },
  icon: {
    fontSize: '1rem',
    marginRight: '0.75rem',
  },
  message: {
    display: 'inline-flex',
    'align-items': 'center',
  },
}));

const getClass = (successBar: boolean, error: boolean, warning: boolean): string => {
  const classes = useStyles();
  if (successBar) {
    return classes.success;
  } else if (error) {
    return classes.error;
  } else if (warning) {
    return classes.warning;
  }
  return classes.info;
};

const getMessageColor = (messageColor: string, warning: boolean) => {
  if (messageColor) {
    return { color: messageColor };
  } else if (warning) {
    return { color: 'black' };
  }
  return {};
};

const getCloseColor = (closeColor: string, warning: boolean) => {
  if (closeColor) {
    return closeColor;
  } else if (warning) {
    return 'black';
  }
  return 'inherit';
};

export const SnackbarView = decorate(
  {},
  function ({
    enableTopPosition,
    enableCenterPosition,
    open,
    error,
    processing,
    warning,
    info,
    children,
    successBar,
    onClose,
    backgroundColor,
    messageColor,
    closeColor,
    autoHideDuration,
  }: ISnackbarProps) {
    const classes = useStyles();
    const snackbarClass = getClass(successBar || false, error || false, warning || false);
    const snackbarMessageColor = getMessageColor(messageColor || '', warning || false);
    const snackbarCloseColor = getCloseColor(closeColor || '', warning || false);

    const getIcon = (): ReactComponentElement<any> => {
      if (warning) {
        return (
          <img
            src='/assets/shared/svg/NoData.svg'
            style={{ width: 35, height: 36, marginRight: '10px' }}
          />
        );
      }
      if (error) {
        return (
          <IconV2 name='alert' color={publicaColors.white} style={{ marginRight: '0.75rem' }} />
        );
      }
      if (processing) {
        return (
          <IconV2
            name='sync'
            color={publicaColors.white}
            style={{ marginRight: '0.75rem' }}
            className='icon-animation-rotate'
          />
        );
      }
      if (info) {
        return (
          <IconV2
            name='info-static'
            color={publicaColors.white}
            style={{ marginRight: '0.75rem' }}
          />
        );
      }

      return (
        <IconV2
          name='success-check-mark'
          color={publicaColors.teal}
          style={{ marginRight: '0.75rem' }}
        />
      );
    };

    return (
      <Snackbar
        anchorOrigin={{
          vertical: enableTopPosition ? 'top' : 'bottom',
          horizontal: enableCenterPosition ? 'center' : 'right',
        }}
        open={open}
        autoHideDuration={autoHideDuration ? autoHideDuration : 6000}
        onClose={onClose}
      >
        <SnackbarContent
          className={snackbarClass}
          aria-describedby='client-snackbar-model-delete-dialog'
          style={
            backgroundColor
              ? { backgroundColor, whiteSpace: 'pre-line' }
              : { whiteSpace: 'pre-line' }
          }
          elevation={2}
          message={
            <span
              id='client-snackbar-model-delete-dialog'
              className={classes.message}
              style={snackbarMessageColor}
            >
              {getIcon()}
              {children}
            </span>
          }
          action={[
            // @ts-ignore
            <IconButton key='close' aria-label='Close' color={snackbarCloseColor} onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>,
          ]}
        />
      </Snackbar>
    );
  },
);
